'use client'

// SUMMARY: In-chat generated-document card - opens the full document EDITOR in a dialog (WYSIWYG editing +
// inline AI edit + PDF preview/download), and offers a direct download on the card. The editor is lazy-loaded
// so it doesn't weigh down the chat bundle; it mounts only when the dialog opens.

import { useState } from 'react'
import dynamic from 'next/dynamic'
import { useLocale, useTranslations } from 'next-intl'
import { Download, FileText, Pencil } from 'lucide-react'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'

const DocumentEditor = dynamic(
	() => import('@/components/documents/editor/document-editor').then((m) => m.DocumentEditor),
	{ ssr: false }
)

// A leading file emoji the model may prepend to the link text (📄/📝/📑).
const FILE_EMOJI = /^\s*[\u{1F4C4}\u{1F4DD}\u{1F4D1}]\s*/u
// The document id inside the download href (/api/ai/documents/<id>).
const DOC_ID = /\/documents\/([^/?]+)/

export function DocumentViewerDialog({ href, label }: { href: string; label: string }) {
	const t = useTranslations('projects')
	const locale = useLocale()
	const [open, setOpen] = useState(false)
	const filename = label.replace(FILE_EMOJI, '').trim() || label
	const docId = href.match(DOC_ID)?.[1] ?? ''
	// Download the freshly rendered (branded) PDF, not the stored bytes which may predate a branding change.
	const downloadHref = docId ? `/api/ai/documents/${docId}/download?locale=${locale}` : href

	return (
		<span className="my-2 inline-flex max-w-full items-center gap-1 rounded-lg border border-border bg-foreground/[0.03] p-1 pl-3 align-middle">
			<button
				type="button"
				onClick={() => setOpen(true)}
				className="flex min-w-0 items-center gap-3 py-1.5 pr-1 text-left"
			>
				<FileText className="h-5 w-5 shrink-0 text-primary" />
				<span className="flex min-w-0 flex-col">
					<span className="truncate text-sm font-medium text-foreground">{filename}</span>
					<span className="flex items-center gap-1 text-xs text-muted-foreground">
						<Pencil className="h-3 w-3" />
						{t('files.open')}
					</span>
				</span>
			</button>
			<a
				href={downloadHref}
				download
				title={t('files.download')}
				aria-label={t('files.download')}
				className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-foreground/[0.06] hover:text-foreground sm:h-8 sm:w-8"
			>
				<Download className="h-4 w-4" />
			</a>

			<Dialog open={open} onOpenChange={setOpen}>
				<DialogContent className="flex h-[92vh] w-[min(calc(100vw-2rem),1100px)] max-w-none flex-col gap-0 overflow-hidden p-0 sm:max-w-[min(calc(100vw-2rem),1100px)]">
					<DialogTitle className="sr-only">{filename}</DialogTitle>
					<DialogDescription className="sr-only">{t('files.open')}</DialogDescription>
					<div className="min-h-0 flex-1 overflow-y-auto p-4">
						{open && docId && <DocumentEditor docId={docId} fill onDeleted={() => setOpen(false)} />}
					</div>
				</DialogContent>
			</Dialog>
		</span>
	)
}
